import { useLazyQuery, useQuery } from "@apollo/client/react";
import SearchIcon from '@mui/icons-material/Search';
import { Box, FormControl, Grid, InputAdornment, InputLabel, MenuItem, Select, TextField, Tooltip } from '@mui/material';
import 'moment/locale/es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import DataGridGenerico from '../../../components/dataGrid/DataGridGenerico';
import { adaptDelegacionReclamosArray } from '../../../adapters/delegacionAdapter';
import { generateColumnsDefaultConfiguration } from '../../../components/dataGrid/columnHelpers';
import { GET_USUARIO_DELEGACION_ORIGEN } from '../../../data/graphQL/queries/usuarios/getUsuarioDelegacionOrigen';
import { usePersistFilters } from '../../../hooks/usePersistFilters';
import ClearIcon from '@mui/icons-material/Clear';
import { PopoverModal } from '../../../components/popoverModal/PopoverModal';
import { CustomButton } from '../../../components/customButton/CustomButton';
import { DateInput } from '../../../components/dateInput/DateInput';
import moment from 'moment';
import { CustomChip } from '../../../components/chip/Chip';
import { useDebounce } from '../../../hooks/useDebounce';
import adaptCapasArray, { GET_CAPAS } from '../../../data/graphQL/queries/Capas/getCapas';
import { GET_ALL_AREAS_CAPA } from '../../../data/graphQL/queries/Reclamos/getAllAreasCapa';
import { GET_ALL_ESTADOS_RECLAMO } from '../../../data/graphQL/queries/Reclamos/getAllEstadosReclamo';
import { GET_ALL_FUENTES } from '../../../data/graphQL/queries/Capas/getAllFuentes';
import HistorialModal from '../../CapaCreation/components/tabs/components/HistorialModal';
import { GET_DELEGACIONES_BY_USUARIO } from "../../../data/graphQL/queries/PacienteProbable/getAllDelegacionesByUsuario";

const storageId = sessionStorage.getItem('idUsuario');

const ESTADO_PE = 'Pendiente';
const ESTADO_AP = 'Aprobado';
const ESTADO_RE = 'Rechazado';
const ESTADO_DE = 'Desestimado';
const ESTADO_CE = 'Cerrado';

const vencimientoOptions = [
  { id: 1, displayName: 'Todos' },
  { id: 2, displayName: 'Proximos a vencer' },
  { id: 3, displayName: 'Vencidos' }];


const chipConfig = {
  [ESTADO_PE]: { isYellow: true, title: ESTADO_PE },
  [ESTADO_AP]: { isGreen: true, title: ESTADO_AP },
  [ESTADO_RE]: { isViolet: true, title: ESTADO_RE },
  [ESTADO_CE]: { isGreen: true, title: ESTADO_CE },
  [ESTADO_DE]: { isRed: true, title: ESTADO_DE }
};

const getChipEstado = (estado) => {
  const config = chipConfig[estado];
  if (config) {
    return <CustomChip
      label={config.title}
      isYellow={config.isYellow}
      isViolet={config.isViolet}
      isGreen={config.isGreen}
      isGray={config.isGray}
      isRed={config.isRed}
      title={config.title} />;

  }
  return null;
};

const opcionTodos = { id: -1, displayName: 'Todos' };

export const CapasContent = ({ setChildrenAction }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const rutasStorage = localStorage.getItem('navigationRoutes');

  const [delegacionOrigen, setDelegacionOrigen] = useState(0);
  const [delegaciones, setDelegaciones] = useState([]);
  const [areasCapa, setAreasCapa] = useState([opcionTodos]);
  const [estadosCapa, setEstadosCapa] = useState([opcionTodos]);
  const [fuentes, setFuentes] = useState([opcionTodos]);
  const [allowedWrite, setAllowedWrite] = useState(false);

  const {
    capas,
    capasLoading,
    totalCount,
    page,
    pageSize,
    filters,
    handleChangeFilters,
    setCapas,
    onPageChange,
    onPageSizeChange
  } = useGetCapas(delegacionOrigen);

  const { data: dataAreasCapa } = useQuery(GET_ALL_AREAS_CAPA);

  useEffect(() => {
    if (!dataAreasCapa) return;
    const areas = dataAreasCapa?.allAreasCapa?.map((area) => ({ id: area.idAreaCapa, displayName: area.descripcion }));
    setAreasCapa([opcionTodos, ...areas]);
  }, [dataAreasCapa]);

  const { data: dataEstados } = useQuery(GET_ALL_ESTADOS_RECLAMO);

  useEffect(() => {
    if (!dataEstados) return;
    const estados = dataEstados?.allEstadosReclamo?.map((estado) => ({ id: estado.idEstadoReclamo, displayName: estado.descripcion }));
    setEstadosCapa([opcionTodos, ...estados]);
  }, [dataEstados]);

  const { data: dataFuentes } = useQuery(GET_ALL_FUENTES);

  useEffect(() => {
    if (!dataFuentes) return;
    const fuentes = dataFuentes?.allFuenteCapa?.map((fuente) => ({ id: fuente.idFuenteCapa, displayName: fuente.descripcion }));
    setFuentes([opcionTodos, ...fuentes]);
  }, [dataFuentes]);

  const getFechaLimite = useCallback((fechaLimite, idEstado) => {
    const now = moment();
    const limiteMoment = moment(fechaLimite);

    const daysDiff = limiteMoment.diff(now, 'days'); // Diferencia en días con respecto a ahora

    let tooltipText = limiteMoment.format('DD/MM/YYYY');
    let sx = {
      width: 'fit-content',
      height: '21px',
      display: 'flex',
      alignItems: 'center',
      padding: '4px 8px',
      borderRadius: 4
    };
    if (idEstado !== 4 && idEstado !== 5) {
      if (daysDiff < 0) {
        // Fecha pasada (vencido)
        const diasVencidos = Math.abs(daysDiff); // Convertir a positivo
        sx = {
          ...sx,
          backgroundColor: '#E71B3E', // Rojo
          color: 'white'
        };
        tooltipText = `${diasVencidos} ${diasVencidos > 1 ? 'días' : 'día'} vencido`;
      } else if (daysDiff <= 20) {
        // Falta 20 días o menos para vencer
        sx = {
          ...sx,
          backgroundColor: '#FBD732', // Amarillo
          color: 'black'
        };
        tooltipText = `${daysDiff} ${daysDiff > 1 ? 'días' : 'día'} para vencer`;
      }
    }

    return (
      <Tooltip title={tooltipText}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Box sx={sx}>
            {limiteMoment.format('DD/MM/YYYY')}
          </Box>
        </Box>
      </Tooltip>);

  }, []);


  const initialColumns = [
    {
      headerName: '',
      field: 'action',
      cellClassName: 'grid-actions-column',
      minWidth: 35,
      maxWidth: 35,
      width: 35,
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return (
          <PopoverModal>
            <div className="popover-modal-content">
              {allowedWrite &&
                row.estado !== 'Cerrado' &&
                row.estado !== 'Desestimado' &&
                row.puedeEditar &&
                <div aria-hidden='true'
                  onClick={() => handleClickEdit(row)}
                  className='item-option clickable-option'
                >
                  <EditIcon className='edit-icon' />
                  <span className='toggle-action'>Editar</span>
                </div>
              }

              <div aria-hidden='true'
                onClick={() => handleClickView(row)}
                className='item-option clickable-option'
              >
                <VisibilityIcon className='edit-icon' />
                <span className='toggle-action'>Ver</span>
              </div>
              <HistorialModal
                isPopoverMenuOption
                idGestionCapa={row.id}
                estados={estadosCapa} />

            </div>
          </PopoverModal>);

      }
    },
    {
      width: 30,
      field: 'id',
      headerName: '#',
      align: 'center',
      headerAlign: 'center'
    },
    {
      width: 190,
      field: 'idDelegacion',
      headerName: 'Delegación',
      valueGetter: (idDelegacion) => {
        return delegaciones.find((delegacion) => delegacion.id === idDelegacion)?.displayName || '-';
      },
      align: 'center',
      headerAlign: 'center'
    },
    {
      width: 130,
      field: 'fechaApertura',
      headerName: 'Fecha de apertura',
      align: 'center',
      headerAlign: 'center'
    },
    {
      width: 220,
      field: 'idArea',
      headerName: 'Área responsable',
      valueGetter: (idArea) => {
        return areasCapa.find((area) => area.id === idArea)?.displayName || '-';
      },
      align: 'center',
      headerAlign: 'center'
    },
    {
      width: 120,
      field: 'idFuente',
      headerName: 'Fuente',
      valueGetter: (idFuente) => {
        return fuentes.find((fuente) => fuente.id === idFuente)?.displayName || '-';
      },
      align: 'center',
      headerAlign: 'center'
    },
    {
      width: 150,
      field: 'estado',
      headerName: 'Estado',
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellProps) => {
        const { row } = cellProps;
        return getChipEstado(row.estado);
      }
    },
    {
      width: 220,
      field: 'descripcionEtapa',
      headerName: 'Etapa actual',
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_, row) => {
        if (row.estado === ESTADO_CE || row.estado === ESTADO_DE) return '-';
        return row.descripcionEtapa;
      }
    },
    {
      width: 180,
      field: 'responsableAccion',
      headerName: 'Resp. Acción',
      align: 'center',
      headerAlign: 'center'
    },
    {
      width: 110,
      field: 'fechaAccion',
      headerName: 'F. Límite',
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        return getFechaLimite(row.fechaAccion, row.idEstado);
      }
    },
    {
      width: 120,
      field: 'fechaCierre',
      headerName: 'F. Cierre de capa',
      align: 'center',
      headerAlign: 'center'
    }];


  const columns = useMemo(() => generateColumnsDefaultConfiguration(initialColumns, true), [delegaciones, capas]);

  const [getDelegaciones, { data: dataDelegaciones, loading: delegacionesLoading }] = useLazyQuery(GET_DELEGACIONES_BY_USUARIO, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataDelegaciones) return;
    setDelegaciones(adaptDelegacionReclamosArray(dataDelegaciones));
  }, [dataDelegaciones]);

  const [getDelegacionOrigen, { data: dataDelegacionOrigen }] = useLazyQuery(GET_USUARIO_DELEGACION_ORIGEN, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataDelegacionOrigen) return;
    if (dataDelegacionOrigen.usuarioFull.success) {
      const deleg = parseInt(dataDelegacionOrigen.usuarioFull.valorEsperado.delegacionOrigen, 10);
      sessionStorage.setItem('delegacionOrigen', deleg);
      setDelegacionOrigen(deleg);

      if (filters.idDelegacion == 0)
        handleChangeFilters('idDelegacion', deleg);
    }
  }, [dataDelegacionOrigen]);

  useEffect(() => {
    if (rutasStorage) {
      const rutas = JSON.parse(rutasStorage);
      const rutaQueSirve = location.pathname;

      setAllowedWrite(rutas?.find((ruta) => ruta.aplicaciones.find((app) => app.url == rutaQueSirve)
      )?.aplicaciones?.find((app) => app.url == rutaQueSirve).escritura);

    }
  }, [rutasStorage]);

  useEffect(() => {
    setChildrenAction(() =>
      <div className='top-buttons'>
        <CustomButton
          label={`Nueva capa`}
          onClick={handleClickCreate}
          variant='contained' />

      </div>
    );
  }, [setChildrenAction, filters?.txtFiltro, delegacionOrigen]);

  useEffect(() => {
    if (!delegacionesLoading) {
      getDelegaciones({ variables: { idUsuario: JSON.parse(storageId, 10) } });
    }
    getDelegacionOrigen({ variables: { idUsuario: JSON.parse(storageId, 10) } });
  }, []);
  useEffect(() => {
    if (filters.tipoVencimiento !== 1)
      handleChangeFilters("idEstado", 1);
  }, [filters.tipoVencimiento]);

  const handleClickCreate = () => {
    navigate(`/calidad/crear/capa`,
      {
        state: {
          idCapa: 0,
          isNew: true,
          allowedWrite: true,
          idEtapa: 0,
          estados: estadosCapa,
          idEstado: 1
        }
      }
    );
  };

  const handleClickEdit = (row) => {
    navigate(`/calidad/crear/capa`,
      {
        state: {
          idCapa: row.id,
          isNew: false,
          allowedWrite: true,
          idEtapa: row.idEtapa,
          idEstado: row.idEstado,
          estados: estadosCapa
        }
      }
    );
  };

  const handleClickView = (row) => {
    navigate(`/calidad/crear/capa`,
      {
        state: {
          idCapa: row.id,
          isNew: false,
          allowedWrite: false,
          idEtapa: row.idEtapa,
          idEstado: row.idEstado,
          estados: estadosCapa
        }
      }
    );
  };

  const handleTextClean = () => {
    handleChangeFilters('txtFiltro', '');
    // handleTextFilter('empty');
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    handleChangeFilters(name, value);
  };

  return (
    <div className='grid-container requisitos-clientes-grilla'>
      <Grid
        container
        columns={24}
        spacing={2}
        sx={{ marginBottom: '25px' }}>

        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 3.5 }}>
          <TextField
            label='Buscar'
            name='txtFiltro'
            size='small'
            type='text'
            fullWidth
            variant='outlined'
            value={filters?.txtFiltro}
            onChange={(e) => handleChangeFilters('txtFiltro', e.target.value)}
            sx={{ backgroundColor: 'white' }}
            InputProps={{
              endAdornment:
                <InputAdornment position='end'>
                  <SearchIcon
                    sx={{ cursor: 'pointer' }} />

                  <ClearIcon
                    onClick={handleTextClean}
                    sx={{ cursor: 'pointer' }} />

                </InputAdornment>

            }} />

        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 3.5 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-delegacion'>Delegación</InputLabel>
            <Select
              labelId='select-delegacion'
              options={delegaciones}
              label='Delegación'
              onChange={handleChange}
              value={filters?.idDelegacion}
              name='idDelegacion'>

              {delegaciones && delegaciones.map((delegacion, index) => {
                return <MenuItem key={delegacion.id + index} value={delegacion.id}> {delegacion.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 3.5 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-delegacion'>Área responsable</InputLabel>
            <Select
              labelId='select-delegacion'
              options={areasCapa}
              label='Área responsable'
              onChange={handleChange}
              value={filters?.idArea}
              name='idArea'>

              {areasCapa && areasCapa.map((areasCapa, index) => {
                return <MenuItem key={areasCapa.id + index} value={areasCapa.id}> {areasCapa.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 2.5 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-delegacion'>Estado</InputLabel>
            <Select
              labelId='select-delegacion'
              options={estadosCapa}
              label='Estado'
              onChange={handleChange}
              value={filters?.idEstado}
              name='idEstado'>

              {estadosCapa && estadosCapa.map((estado, index) => {
                return <MenuItem key={estado.id + index} value={estado.id}> {estado.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 2.5 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-delegacion'>Fuentes</InputLabel>
            <Select
              labelId='select-delegacion'
              options={fuentes}
              label='Fuentes'
              onChange={handleChange}
              value={filters?.idFuente}
              name='idFuente'>

              {fuentes && fuentes.map((fuente, index) => {
                return <MenuItem key={fuente.id + index} value={fuente.id}> {fuente.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 2.5 }}>
          <DateInput
            label="Fecha Desde"
            disabled={filters?.tipoVencimiento !== 1}
            name='fechaDesde'
            value={filters?.fechaDesde}
            onChange={(value) => handleChangeFilters('fechaDesde', moment(value).format('yyyy-MM-DD HH:mm:ss'))} />

        </Grid>
        <Grid size={{ xs: 24, sm: 12, md: 6, lg: 2.5 }}>
          <DateInput
            label="Fecha Hasta"
            disabled={filters?.tipoVencimiento !== 1}
            name='fechaHasta'
            value={filters?.fechaHasta}
            onChange={(value) => handleChangeFilters('fechaHasta', moment(value).set({ hour: 23, minute: 59, second: 59 }).format('yyyy-MM-DD HH:mm:ss'))} />

        </Grid>
        <Grid sx={{ display: 'flex', alignItems: 'center' }} size={{ xs: 24, sm: 12, md: 6, lg: 3.5 }}>
          <FormControl sx={{ backgroundColor: 'white', width: '100%' }} size='small'>
            <InputLabel id='select-vencimiento'>Vencimiento</InputLabel>
            <Select
              labelId='select-vencimiento'
              options={vencimientoOptions}
              label='Vencimiento'
              onChange={handleChange}
              value={filters?.tipoVencimiento || undefined}
              name='tipoVencimiento'>

              {vencimientoOptions && vencimientoOptions.map((tipoVencimiento) => {
                return <MenuItem key={tipoVencimiento.id} value={tipoVencimiento.id}> {tipoVencimiento.displayName} </MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <DataGridGenerico
        columns={columns}
        totalCount={totalCount}
        rows={capas}
        toolbar={false}
        loading={capasLoading}
        currentPage={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        maxWidth={columns.reduce((total, column) => total + (column.width || 0), 0)} />

    </div>);

};

const useGetCapas = (delegacionOrigen) => {
  const [capas, setCapas] = useState([]);
  const [paginationStatus, setPaginationStatus] = useState({
    pageSize: 10,
    page: 0
  });

  const { filters, handleChangeFilters } = usePersistFilters({
    idDelegacion: delegacionOrigen,
    idEstado: 1,
    idArea: -1,
    idFuente: -1,
    fechaDesde: null,
    fechaHasta: moment().add(20, 'd').format("yyyy-MM-DD"),
    txtFiltro: '',
    tipoVencimiento: 1
  }, `capaFilter`);

  const debouncedTextFilter = useDebounce(filters.txtFiltro, 1000);

  const buildFilters = (filterValue) => {
    if (!filterValue) return null;

    // Divide el valor en palabras
    const terms = filterValue.trim().split(/\s+/);

    // Si solo hay un término, busca en todos los campos relevantes individualmente
    if (terms.length === 1) {
      return {
        or: [
          { idResponsableAccionNavigation: { nombre: { contains: terms[0] } } },
          { idResponsableAccionNavigation: { apellido: { contains: terms[0] } } }]

      };
    }

    return {
      and: terms.map((term) => ({
        or: [
          { idResponsableAccionNavigation: { nombre: { contains: term } } },
          { idResponsableAccionNavigation: { apellido: { contains: term } } }]

      }))
    };
  };

  const [getCapas, { data: dataCapas, capasLoading }] = useLazyQuery(GET_CAPAS, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!dataCapas) return;
    setCapas(dataCapas);
  }, [dataCapas]);

  useEffect(() => {
    if (capasLoading) return;
    getCapas({
      variables: {
        skip: paginationStatus.page * paginationStatus.pageSize,
        take: paginationStatus.pageSize,
        order: { idGestionCapa: "DESC" },
        where: {
          and: [
            !!debouncedTextFilter ? {
              or: [
                debouncedTextFilter ? buildFilters(debouncedTextFilter) ?? {} : {},
                parseInt(debouncedTextFilter) > 0 ? { idGestionCapa: { eq: parseInt(debouncedTextFilter) } } : {},
                { idEtapaNavigation: { descripcion: { contains: debouncedTextFilter } } }]

            } : {},
            filters.idDelegacion > -1 ?
              { idDelegacion: { eq: filters.idDelegacion } } :
              {},
            filters.idEstado !== -1 //Si la opcion es -1 es todos, se borra el filtro
              ? { idEstado: { eq: filters.idEstado } } :
              {},
            filters.idArea !== -1 //Si la opcion es -1 es todos, se borra el filtro
              ? { idArea: { eq: filters.idArea } } :
              {},
            filters.idFuente !== -1 //Si la opcion es -1 es todos, se borra el filtro
              ? { idFuente: { eq: filters.idFuente } } :
              {}]

        },
        filtroFechaVencida: filters.tipoVencimiento
      }
    });
  }, [filters, debouncedTextFilter, paginationStatus.page]);

  const onPageChange = (page) => {
    setPaginationStatus((pagStatus) => ({ ...pagStatus, page: page }));
  };

  const onPageSizeChange = (pageSize) => {
    setPaginationStatus((pagStatus) => ({ ...pagStatus, pageSize: pageSize, page: 0 }));
  };

  return {
    capas: adaptCapasArray(capas),
    capasLoading,
    totalCount: capas?.allGestionCapa?.totalCount,
    page: paginationStatus.page,
    pageSize: paginationStatus.pageSize,
    filters,
    handleChangeFilters,
    setCapas,
    onPageChange,
    onPageSizeChange
  };
};